<script setup lang="ts">
/**
 * The ALTCHA widget fitted into a kit well with a shield beside it.
 *
 * The template's auth wants a solved ALTCHA payload on register, login,
 * forgot- and reset-password, and a payload is single-use server-side: after a
 * refused submit the caller calls `rerun()`, which clears the payload and
 * makes the widget solve a fresh challenge. The payload is the v-model; the
 * empty string means "not solved".
 */
import { ref } from 'vue';


withDefaults(defineProps<{ testId?: string }>(), { testId: undefined });

const payload = defineModel<string>({ default: '' });

const ALTCHA_CONFIG = JSON.stringify({ hideFooter: true, hideLogo: false });

type AltchaElement = HTMLElement & { reset?: () => void; verify?: () => void };
const widget = ref<AltchaElement | null>(null);

function onState(event: Event): void {
  const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
  payload.value = detail?.state === 'verified' ? (detail.payload ?? '') : '';
}

function rerun(): void {
  payload.value = '';
  widget.value?.reset?.();
  widget.value?.verify?.();
}

defineExpose({ rerun });
</script>

<template>
  <div class="k-well captcha">
    <altcha-widget
      ref="widget"
      class="altcha"
      challenge="/api/auth/altcha-challenge"
      auto="onload"
      name="altcha"
      :configuration="ALTCHA_CONFIG"
      :data-test="testId"
      @statechange="onState"
    ></altcha-widget>
  </div>
</template>

<style scoped>
.captcha {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 8px 8px 10px;
}

/*
 * The widget's own stylesheet is injected at runtime, after this one, and
 * hides the host until it decides otherwise; a scoped :deep rule outranks it.
 * style.css inks it for dark steel; here it also drops its box, because the
 * well is the box.
 */
.captcha :deep(altcha-widget),
.captcha :deep(.altcha) {
  display: block;
  flex: 1;
  min-width: 0;
  --altcha-color-base: transparent;
  --altcha-border-width: 0;
  --altcha-border-radius: 8px;
  --altcha-shadow: none;
  --altcha-max-width: 100%;
}
</style>
